// Couche d'appel de l'Edge Function d'envoi de l'e-mail de validation.
//
// Toute la logique sensible (vérification Super Admin, destinataire réel,
// idempotence, envoi Resend, suivi) reste côté serveur dans la fonction
// `envoyer-email-validation`. Le frontend ne fait que la déclencher avec
// l'identifiant du paiement — jamais un montant, un rôle ou un e-mail.

import { supabase } from "@/store/shared/supabase";

export interface EmailValidationResult {
  ok: boolean;
  /** `true` uniquement lorsqu'un e-mail a réellement été envoyé à cet appel. */
  envoye: boolean;
  /** Motif lisible lorsqu'aucun e-mail n'a été envoyé (déjà envoyé, non validé…). */
  raison?: string;
  /** Détail technique éventuel (config manquante, erreur fournisseur…). */
  detail?: string;
  email?: string;
}

/**
 * Déclenche l'envoi de l'e-mail de validation pour un paiement donné.
 * Le serveur décide seul s'il faut réellement envoyer (paiement validé, pas
 * encore envoyé). Idempotent : un paiement déjà notifié n'enverra rien de plus.
 */
export async function envoyerEmailValidationPaiement(paiementId: string): Promise<EmailValidationResult> {
  const { data, error } = await supabase.functions.invoke("envoyer-email-validation", {
    body: { paiementId },
  });

  if (error) {
    // Une Edge Function en erreur répond en JSON : on remonte le VRAI motif
    // plutôt qu'un simple code HTTP, sans jamais exposer de secret.
    let serverMessage = "";
    const context = (error as { context?: Response }).context;
    if (context && typeof context.clone === "function") {
      try {
        const parsed = (await context.clone().json()) as { error?: string; detail?: string } | null;
        serverMessage = [parsed?.error, parsed?.detail].filter(Boolean).join(" — ");
      } catch {
        try {
          const text = await context.clone().text();
          if (text) serverMessage = text;
        } catch {
          /* corps illisible : on garde le message générique */
        }
      }
    }
    throw new Error(serverMessage || error.message || "Envoi de l'e-mail impossible");
  }

  return (data as EmailValidationResult | null) ?? { ok: false, envoye: false };
}